import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Bell, CheckCircle2, Package, ShoppingCart } from "lucide-react";
import api from "../lib/api";
import PageMeta from "../components/PageMeta";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import RelatedRow from "../components/RelatedRow";
import TonerPriceTable from "../components/TonerPriceTable";

const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;

/**
 * Shared SEO page renderer for both `/toner/:slug` (laser powder cartridges)
 * and `/consumable/:slug` (ink/drum/ribbon/fuser/maintenance). Reads `kind`
 * from the API and redirects to the canonical URL when the caller used the
 * wrong route — that's our 301-style client fix for old /toner/:slug links
 * that were actually inks.
 */
export default function TonerModelPage({ pageKind = "toner" }) {
    const params = useParams();
    const slug = params.slug || params.id;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [email, setEmail] = useState("");
    const [notified, setNotified] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const firstRowRef = useRef(null);
    const [showBar, setShowBar] = useState(false);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setNotFound(false);
        const endpoint = pageKind === "consumable" ? "consumable-page" : "toner-page";
        api.get(`/compat/${endpoint}/${slug}`)
            .then((r) => { if (active) setData(r.data); })
            .catch((e) => { if (active && e?.response?.status === 404) setNotFound(true); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [slug, pageKind]);

    // Show the sticky "lowest price" bar once the first dealer row scrolls above the viewport.
    useEffect(() => {
        const el = firstRowRef.current;
        if (!el) { setShowBar(false); return; }
        const obs = new IntersectionObserver(
            ([e]) => setShowBar(!e.isIntersecting && e.boundingClientRect.top < 0),
            { threshold: 0 }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [data]);

    const submitNotify = async (e) => {
        e.preventDefault();
        if (!email.trim()) { toast.error("Enter your email"); return; }
        setSubmitting(true);
        try {
            // Reuse the notify capture, keyed by the first compatible printer (or the toner slug).
            const firstPrinter = data?.compatible_printers?.[0];
            await api.post("/compat/notify", {
                printer_slug: firstPrinter?.slug || `toner-${slug}`,
                printer_name: data?.toner?.model ? `${data.toner.brand} ${data.toner.model}` : slug,
                email: email.trim(),
            });
            setNotified(true);
            toast.success("We'll email you when stock is available.");
        } catch { toast.error("Couldn't save your request. Please try again."); }
        finally { setSubmitting(false); }
    };

    if (loading) {
        return <div className="tc-container py-20 text-center text-[#6E6E73]" data-testid="toner-loading">Loading…</div>;
    }
    if (notFound || !data) {
        return (
            <div className="tc-container py-20 text-center" data-testid="toner-notfound">
                <PageMeta title="Cartridge not found — TonersCart" description="This cartridge model was not found." path={`/${pageKind}/${slug}`} />
                <h1 className="text-2xl font-semibold text-[#0A0A0B]">Cartridge model not found</h1>
                <p className="mt-2 text-[#6E6E73]">We couldn&rsquo;t find this cartridge in our database.</p>
                <Link to="/search" className="inline-flex items-center gap-1 mt-5 text-[#00B7C7] font-semibold">Browse all products <ArrowRight size={16} /></Link>
            </div>
        );
    }

    // 301-style client redirect: ink/drum requested at /toner/:slug → push to
    // /consumable/:slug (and vice versa). Search engines see a canonical link
    // via PageMeta, users land on the right URL within milliseconds.
    const correctKind = data.kind || (data.toner?.type === "toner" ? "toner" : "consumable");
    if (correctKind !== pageKind) {
        const canonical = data.canonical_url || `/${correctKind}/${data.toner?.slug || slug}`;
        return <Navigate to={canonical} replace />;
    }

    const t = data.toner;
    const listings = data.listings || [];
    const printers = data.compatible_printers || [];
    const related = data.related || {};
    const title = `${t.brand} ${t.model} Price India — Buy from Verified Dealers | TonersCart`;
    const description = `Buy ${t.brand} ${t.model} at best price in India. Compare prices from verified dealers. GST invoice guaranteed. Pan-India delivery.`;

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": `${t.brand} ${t.model}`,
        "brand": { "@type": "Brand", "name": t.brand },
        "category": t.type,
        "url": `https://www.tonerscart.com/${pageKind}/${t.slug}`,
        ...(listings.length > 0 ? {
            "offers": {
                "@type": "AggregateOffer",
                "priceCurrency": "INR",
                "lowPrice": String(Math.min(...listings.map((l) => Number(l.price) || 0))),
                "highPrice": String(Math.max(...listings.map((l) => Number(l.price) || 0))),
                "offerCount": listings.length,
                "availability": "https://schema.org/InStock",
            },
        } : {}),
    };

    return (
        <div className="bg-[#FBFBFC] min-h-screen" data-testid="toner-page">
            <PageMeta title={title} description={description} path={`/${pageKind}/${t.slug}`} jsonLd={jsonLd} />

            {/* Hero */}
            <section className="tc-hero tc-hero-home relative pt-10 pb-10">
                <div className="tc-container">
                    <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.16em] uppercase text-white/70 font-semibold">
                        <Package size={13} /> {t.brand} · {t.type}
                    </div>
                    <h1 className="mt-2 text-white text-3xl sm:text-4xl" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }} data-testid="toner-h1">
                        {t.brand} {t.model} — Compatible Printers &amp; Verified Dealers
                    </h1>
                    <p className="mt-2 text-white/75 text-[14px] max-w-2xl">
                        Compare {t.model} prices from verified dealers across India — GST invoice on every order, pan-India delivery.
                    </p>
                </div>
            </section>

            <div className="tc-container py-10 space-y-10">
                {/* Suitable for */}
                {printers.length > 0 && (
                    <section data-testid="toner-compatible-printers">
                        <h2 className="text-[18px] font-semibold text-[#0A0A0B] mb-3">Suitable for</h2>
                        <div className="flex flex-wrap gap-2">
                            {printers.map((pr) => (
                                <Link key={pr.slug} to={pr.url} className="inline-flex items-center gap-1.5 bg-white border border-[#E5E5EA] rounded-full px-3 py-1.5 text-[13px] text-[#0A0A0B] hover:border-[#00B7C7]/60 hover:text-[#0A6E78] transition" data-testid="toner-compatible-printer-chip">
                                    <span className="font-semibold">{pr.full_name}</span>
                                </Link>
                            ))}
                        </div>
                    </section>
                )}

                {/* Live dealer listings */}
                <section data-testid="toner-listings-section">
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-[18px] font-semibold text-[#0A0A0B]">Available from verified dealers</h2>
                        {listings.length > 0 && <span className="text-[12.5px] text-[#86868B]">{listings.length} listing{listings.length > 1 ? "s" : ""}</span>}
                    </div>
                    {listings.length === 0 ? (
                        <div className="text-[13.5px] text-[#6E6E73]" data-testid="toner-no-stock">No verified dealer has listed {t.model} yet — see the suggestions below, or get notified when stock arrives.</div>
                    ) : (
                        <TonerPriceTable listings={listings} firstRowRef={firstRowRef} />
                    )}
                </section>

                {/* Related / You may also need */}
                {(related.same_printers_toners?.length > 0 || related.same_brand_toners?.length > 0) && (
                    <section className="space-y-6" data-testid="toner-related">
                        <h2 className="text-[18px] font-semibold text-[#0A0A0B]">You may also need</h2>
                        <RelatedRow
                            label="Compatible with same printers"
                            kind="toner"
                            testid="related-same-printers"
                            items={related.same_printers_toners || []}
                        />
                        <RelatedRow
                            label="From the same brand"
                            kind="toner"
                            testid="related-same-brand"
                            items={related.same_brand_toners || []}
                        />
                    </section>
                )}

                {/* Notify me — only when no dealer stock */}
                {listings.length === 0 && (
                    <section data-testid="toner-notify-section">
                        {/* Wave 51: on /consumable/:slug specifically, surface the
                            compatible-printers chip rail right above Notify-Me so
                            buyers who scrolled past the hero still see the full
                            printer set — and Google sees the inbound link juice. */}
                        {pageKind === "consumable" && printers.length > 0 && (
                            <div className="mb-6 bg-white border border-[#E5E5EA] rounded-2xl p-5" data-testid="consumable-compatible-printers-rail">
                                <div className="text-[11px] uppercase tracking-[0.16em] text-[#86868B] font-semibold mb-2">Compatible printers</div>
                                <h3 className="text-[15.5px] font-semibold text-[#0A0A0B] mb-3">
                                    {t.brand} {t.model} fits these printers
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {printers.map((pr) => (
                                        <Link
                                            key={`rail-${pr.slug}`}
                                            to={pr.url}
                                            className="inline-flex items-center gap-1.5 bg-[#F2FBFC] border border-[#C2EFF5] rounded-full px-3 py-1.5 text-[13px] text-[#0A6E78] hover:bg-[#D6F5F9] hover:border-[#00B7C7] transition"
                                            data-testid="consumable-compatible-printer-chip"
                                        >
                                            <span className="font-semibold">{pr.full_name}</span>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="bg-white border border-[#E5E5EA] rounded-2xl p-8 text-center max-w-xl" data-testid="toner-notify-block">
                            <Bell size={26} className="mx-auto text-[#00B7C7]" />
                            <div className="mt-3 text-[16px] font-semibold text-[#0A0A0B]">Notify me when available</div>
                            <p className="mt-1 text-[13.5px] text-[#6E6E73]">Be the first to know when a verified dealer lists {t.brand} {t.model}.</p>
                            {notified ? (
                                <div className="mt-4 inline-flex items-center gap-2 text-[#0A6E78] font-semibold" data-testid="toner-notify-success">
                                    <CheckCircle2 size={18} /> You&rsquo;re on the list!
                                </div>
                            ) : (
                                <form onSubmit={submitNotify} className="mt-4 flex flex-col sm:flex-row gap-2 max-w-md mx-auto">
                                    <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="tc-input-lg" data-testid="toner-notify-email" />
                                    <Button type="submit" className="btn-pill-cta whitespace-nowrap" disabled={submitting} data-testid="toner-notify-submit">
                                        {submitting ? "Saving…" : "Notify me"}
                                    </Button>
                                </form>
                            )}
                        </div>
                    </section>
                )}

                <Link to="/search" className="inline-flex items-center gap-1 text-[#00B7C7] font-semibold" data-testid="toner-browse-all">
                    Browse all products <ArrowRight size={16} />
                </Link>
            </div>

            {/* Sticky lowest-price bar — appears after scrolling past the first dealer row */}
            {listings.length > 0 && showBar && (
                <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#0A0A0B] text-white shadow-2xl" data-testid="toner-sticky-bar">
                    <div className="tc-container py-3 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-[0.16em] text-white/60 font-semibold">Lowest price</div>
                            <div className="text-[18px] font-semibold font-mono truncate">{inr(listings[0].total_price)} <span className="text-[12px] text-white/60 font-sans">incl. GST · {listings[0].dealer_name}</span></div>
                        </div>
                        <Link to={listings[0].url} className="btn-pill-cta whitespace-nowrap inline-flex items-center gap-1.5" data-testid="toner-sticky-buy">
                            <ShoppingCart size={15} /> Buy now
                        </Link>
                    </div>
                </div>
            )}
        </div>
    );
}
